import { CustomToast } from "./custom-toast";
import { ToastConfig } from "@/stores/toast.store";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

interface ToastProviderProps {
  children: React.ReactNode;
}

type ToastListener = (config: ToastConfig) => void;
type DismissListener = (toastId?: string) => void;

let toastListener: ToastListener | null = null;
let dismissListener: DismissListener | null = null;

export function registerToastListeners(onShow: ToastListener, onDismiss: DismissListener) {
  toastListener = onShow;
  dismissListener = onDismiss;
  return () => {
    toastListener = null;
    dismissListener = null;
  };
}

export function emitToast(config: ToastConfig) {
  toastListener?.(config);
}

export function emitDismiss(toastId?: string) {
  dismissListener?.(toastId);
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<ToastConfig[]>([]);
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const location = useLocation();

  const handleExited = useCallback((toastId: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== toastId));
    setExitingIds((prev) => {
      const s = new Set(prev);
      s.delete(toastId);
      return s;
    });
  }, []);

  useEffect(() => {
    const unregister = registerToastListeners(
      (config) => {
        setToasts((prev) => {
          const filtered = prev.filter(
            (t) => !(t.message === config.message && t.type === config.type),
          );
          return [...filtered, config];
        });
        const timer = setTimeout(() => {
          setExitingIds((prev) => new Set([...prev, config.id]));
          timersRef.current.delete(config.id);
        }, config.duration ?? 3000);
        timersRef.current.set(config.id, timer);
      },
      (toastId) => {
        if (toastId) {
          const timer = timersRef.current.get(toastId);
          if (timer) clearTimeout(timer);
          timersRef.current.delete(toastId);
          setExitingIds((prev) => new Set([...prev, toastId]));
        } else {
          timersRef.current.forEach((timer) => clearTimeout(timer));
          timersRef.current.clear();
          setToasts([]);
          setExitingIds(new Set());
        }
      },
    );
    return unregister;
  }, []);

  useEffect(() => {
    emitDismiss();
  }, [location.pathname]);

  return (
    <>
      {children}
      {/* Portal to <body>, not <ion-app>: ion-app is z-index 0 (Ionic's .ion-page),
          so anything inside it sits under overlays portalled to <body>, e.g. the
          Settings upgrade screen at z-[1000]. */}
      {toasts.length > 0 &&
        createPortal(
          <div
            className="pointer-events-none fixed inset-x-0 bottom-8 z-[999999999] flex flex-col items-center gap-2"
            aria-live="polite"
            aria-atomic="false"
          >
            {toasts.map((toast) => (
              <CustomToast
                key={toast.id}
                toast={toast}
                isExiting={exitingIds.has(toast.id)}
                onExited={() => handleExited(toast.id)}
              />
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};
